import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Dock Region Component
 * 
 * Represents a dockable region that can contain tab groups or panels.
 * Supports horizontal or vertical orientation and resizable splitters.
 */
@customElement('dock-region')
export class DockRegion extends LitElement {
  static styles = css`
    :host {
      display: flex;
      position: relative;
      background: transparent;
      /* border: 1px solid rgba(255, 255, 255, 0.1); */
      border-radius: 8px;
      overflow: hidden;
      pointer-events: auto;
      transition: all 0.3s ease-in-out;
    }
    
    :host(.collapsed) {
      background: transparent;
      border: none;
      border-radius: 0;
      overflow: visible;
      min-width: 0;
      min-height: 0;
    }
    
    :host(.collapsed[orientation="horizontal"]) {
      width: 40px !important;
    }
    
    :host(.collapsed[orientation="vertical"]) {
      height: 40px !important;
    }
    
    :host(.collapsed) .region-content {
      display: none;
    }
    
    :host(.collapsed) .resize-handle {
      display: none;
    }
    
    .expand-button {
      display: none;
      position: absolute;
      padding: 8px 12px;
      background: rgba(20, 20, 25, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 6px;
      color: #aaa;
      cursor: pointer;
      font-size: 14px;
      transition: all 0.2s;
      z-index: 100;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    }
    
    .expand-button:hover {
      background: rgba(33, 150, 243, 0.2);
      border-color: rgba(33, 150, 243, 0.5);
      color: #fff;
      transform: scale(1.05);
    }
    
    :host(.collapsed) .expand-button {
      display: block;
    }
    
    /* Position expand button based on region */
    :host(.collapsed[regionId="left"]) .expand-button {
      left: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
    
    :host(.collapsed[regionId="right"]) .expand-button {
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
    
    :host(.collapsed[regionId="top"]) .expand-button {
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
    }
    
    :host(.collapsed[regionId="bottom"]) .expand-button {
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%);
    }

    :host(.drag-over) {
      border-color: rgba(33, 150, 243, 0.8);
      box-shadow: 0 0 16px rgba(33, 150, 243, 0.4);
    }

    :host([orientation="horizontal"]) {
      flex-direction: row;
    }

    :host([orientation="vertical"]) {
      flex-direction: column;
    }

    .region-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      overflow: hidden;
    }

    .resize-handle {
      position: absolute;
      background: rgba(255, 255, 255, 0.05);
      transition: background 0.2s;
      z-index: 10;
    }

    .resize-handle:hover {
      background: rgba(33, 150, 243, 0.3);
    }

    .resize-handle.dragging {
      background: rgba(33, 150, 243, 0.5);
    }

    :host([orientation="horizontal"]) .resize-handle {
      right: 0;
      top: 0;
      width: 4px;
      height: 100%;
      cursor: ew-resize;
    }

    :host([orientation="vertical"]) .resize-handle {
      bottom: 0;
      left: 0;
      width: 100%;
      height: 4px;
      cursor: ns-resize;
    }

    :host([orientation="horizontal"]) .resize-handle:hover,
    :host([orientation="horizontal"]) .resize-handle.dragging {
      width: 6px;
    }

    :host([orientation="vertical"]) .resize-handle:hover,
    :host([orientation="vertical"]) .resize-handle.dragging {
      height: 6px;
    }
  `;

  @property({ type: String, reflect: true }) orientation: 'horizontal' | 'vertical' = 'vertical';
  @property({ type: String, reflect: true }) regionId: 'left' | 'right' | 'top' | 'bottom' = 'left';
  @property({ type: Number }) size = 300;
  @property({ type: Number }) minSize = 200;
  @property({ type: Number }) maxSize = 800;
  @property({ type: Boolean }) resizable = true;
  @property({ type: Boolean }) collapsed = false;

  private isDragging = false;
  private startPos = 0;
  private startSize = 0;

  connectedCallback(): void {
    super.connectedCallback();
    this.updateSize();
    this.addEventListener('dragover', this.handleDragOver);
    this.addEventListener('dragleave', this.handleDragLeave);
    this.addEventListener('drop', this.handleDrop);
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('size') || changed.has('orientation')) {
      this.updateSize();
    }
    if (changed.has('collapsed')) {
      if (this.collapsed) {
        this.classList.add('collapsed');
      } else {
        this.classList.remove('collapsed');
      }
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('dragover', this.handleDragOver);
    this.removeEventListener('dragleave', this.handleDragLeave);
    this.removeEventListener('drop', this.handleDrop);
  }

  private handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
    this.classList.add('drag-over');
  };

  private handleDragLeave = (e: DragEvent) => {
    // Only remove drag-over if we're leaving the region entirely
    const rect = this.getBoundingClientRect();
    if (
      e.clientX < rect.left ||
      e.clientX >= rect.right ||
      e.clientY < rect.top ||
      e.clientY >= rect.bottom
    ) {
      this.classList.remove('drag-over');
    }
  };

  private handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    this.classList.remove('drag-over');

    const panelId = e.dataTransfer?.getData('text/panel-id');
    if (panelId) {
      this.dispatchEvent(new CustomEvent('panel-drop', {
        detail: { panelId, targetRegion: this.regionId },
        bubbles: true,
        composed: true,
      }));
    }
  };

  private updateSize(): void {
    if (this.orientation === 'horizontal') {
      this.style.width = `${this.size}px`;
      this.style.height = '100%';
    } else {
      this.style.width = '100%';
      this.style.height = `${this.size}px`;
    }
  }

  private handleResizeStart = (e: MouseEvent) => {
    if (!this.resizable) return;
    
    e.preventDefault();
    this.isDragging = true;
    this.startPos = this.orientation === 'horizontal' ? e.clientX : e.clientY;
    this.startSize = this.size;

    const handle = this.shadowRoot?.querySelector('.resize-handle');
    if (handle) handle.classList.add('dragging');

    window.addEventListener('mousemove', this.handleResizeMove);
    window.addEventListener('mouseup', this.handleResizeEnd);
  };

  private handleResizeMove = (e: MouseEvent) => {
    if (!this.isDragging) return;

    const currentPos = this.orientation === 'horizontal' ? e.clientX : e.clientY;
    const delta = currentPos - this.startPos;
    const newSize = Math.min(this.maxSize, Math.max(this.minSize, this.startSize + delta));

    this.size = newSize;
    this.updateSize();

    this.dispatchEvent(new CustomEvent('resize', {
      detail: { size: newSize },
      bubbles: true,
      composed: true,
    }));
  };

  private handleResizeEnd = () => {
    this.isDragging = false;

    const handle = this.shadowRoot?.querySelector('.resize-handle');
    if (handle) handle.classList.remove('dragging');

    window.removeEventListener('mousemove', this.handleResizeMove);
    window.removeEventListener('mouseup', this.handleResizeEnd);
  };
  
  private handleExpand = () => {
    this.collapsed = false;
    this.dispatchEvent(new CustomEvent('expand', {
      bubbles: true,
      composed: true,
    }));
  };

  render() {
    return html`
      <div class="region-content">
        <slot></slot>
      </div>
      ${this.resizable ? html`
        <div class="resize-handle" @mousedown=${this.handleResizeStart}></div>
      ` : ''}
      <button class="expand-button" @click=${this.handleExpand} title="Expand panel">
        ${this.regionId === 'left' ? '▶' : this.regionId === 'right' ? '◀' : this.regionId === 'top' ? '▼' : '▲'}
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dock-region': DockRegion;
  }
}
